import React from "react";
import { Table, Skeleton } from "antd";

const Top10Table = ({
  data = [],
  columns = [],
  loading = false,
  tableStyle = {},
  headingBgColor = "#DDE1E6",
  headingTextColor = "#0F161E",
  rowKey = "_id",
  highlightedRowId = null,
  onRowClick = null,
}) => {
  // Ensure we always have exactly 10 rows
  const tableData = React.useMemo(() => {
    const rows = [];
    
    for (let i = 1; i <= 10; i++) {
      const existingRow = data.find(item => item.position === i);
      
      if (existingRow) {
        rows.push({
          ...existingRow,
          _id: existingRow._id || `row-${i}`,
          position: i,
        });
      } else {
        // Create placeholder row for unassigned positions
        rows.push({
          _id: `unassigned-${i}`,
          position: i,
          title: null,
          artist: null,
          trackId: null,
          isAssigned: false,
        });
      }
    }
    
    return rows;
  }, [data]);

  // Enhanced columns with proper styling and null handling
  const enhancedColumns = columns.map((col) => ({
    ...col,
    onCell: () => ({
      style: {
        maxWidth: col.maxWidth || "auto",
        whiteSpace: col.maxWidth ? "nowrap" : "pre-wrap",
        overflow: col.maxWidth ? "hidden" : "visible",
        textOverflow: col.maxWidth ? "ellipsis" : "clip",
        minWidth: col.minWidth || "100px",
      },
    }),
    render: col.render || ((text, record) => {
      // Handle null/undefined values gracefully
      if (text === null || text === undefined) {
        return (
          <span style={{ color: "#999", fontStyle: "italic" }}>
            Not Assigned
          </span>
        );
      }
      
      // Handle nested data paths
      if (typeof col.dataIndex === "string") {
        return text;
      }
      
      if (Array.isArray(col.dataIndex)) {
        let value = record;
        for (const key of col.dataIndex) {
          value = value?.[key];
        }
        return value || (
          <span style={{ color: "#999", fontStyle: "italic" }}>
            Not Assigned
          </span>
        );
      }
      
      return text;
    }),
  }));

  // Skeleton loader for loading state
  const renderSkeleton = () => {
    const skeletonRows = Array(10)
      .fill(null)
      .map((_, index) => ({
        _id: `skeleton-${index + 1}`,
        position: index + 1,
        isSkeletonRow: true,
      }));

    const skeletonColumns = enhancedColumns.map((col) => ({
      ...col,
      render: (text, record) => {
        if (record.isSkeletonRow) {
          return (
            <Skeleton.Button
              active
              style={{ width: "100%", height: 20 }}
              size="small"
            />
          );
        }
        return col.render ? col.render(text, record) : text;
      },
    }));

    return (
      <Table
        columns={skeletonColumns}
        dataSource={skeletonRows}
        pagination={false}
        rowKey="_id"
        style={{
         background: "#191919",
          minWidth: "100%",
          border: "none",
        }}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  backgroundColor: headingBgColor,
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: headingTextColor,
                  letterSpacing: "0.014em",
                  padding: "12px",
                  borderBottom: "1px solid #505050",
                  lineHeight: "22px",
                }}
              />
            ),
          },
          body: {
            cell: (props) => (
              <td
                {...props}
                style={{
                  fontSize: "12px",
                  fontFamily: "Public Sans",
                  color: "#E2E2E2",
                  padding: "12px",
                  lineHeight: "20px",
                  borderBottom: "1px solid #505050",
                  background: "#191919",
                }}
              />
            ),
          },
        }}
      />
    );
  };

  if (loading) {
    return renderSkeleton();
  }

  return (
    <div>
      <div className="w-full overflow-x-auto"
       style={{
          borderRadius: "8px",
          border: "1px solid #dde1e6",
          background: "#191919",
          marginTop: "24px",
          ...tableStyle,
        }}>
        <Table
          columns={enhancedColumns}
          dataSource={tableData}
          rowKey={rowKey}
          pagination={false}
          loading={false}
          rowClassName={(record, index) => {
            let className = index % 2 === 0 ? "even-row" : "odd-row";
            
            // Add highlighted class if this row is highlighted
            if (record && record._id === highlightedRowId) {
              className += " highlighted-row";
            }
            
            // Add unassigned class for styling unassigned rows differently
            if (record && !record.isAssigned) {
              className += " unassigned-row";
            }
            
            return className;
          }}
          onRow={(record) => ({
            onClick: () => {
              if (onRowClick) {
                onRowClick(record);
              }
            },
            style: {
              cursor: onRowClick ? "pointer" : "default",
            },
          })}
          style={{           
            border: "none",
            background: "#191919",
            minWidth: "100%",
            
          }}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  backgroundColor: headingBgColor,
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: headingTextColor,
                  letterSpacing: "0.014em",
                  padding: "8px 16px",
                  borderBottom: "1px solid #505050",
                  lineHeight: "22px",
                   transition: "background 0.2s, border-color 0.2s", 
                   
                }}
              />
            ),
          },
          body: {
            cell: (props) => (
              <td
                {...props}
                style={{
                  fontSize: "12px",
                  fontFamily: "Public Sans",
                  color: "#E2E2E2",
                  lineHeight: "20px",
                  borderBottom: "1px solid #505050",
                   transition: "background 0.2s, border-color 0.2s", 
                   
                }}
              />
            ),
          },
        }}
      />
      
     
      </div>
    </div>
  );
};

export default Top10Table;